import { CloseOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Result, Row, Space, Spin } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { StatePieceStatus } from '../../../../../enums';
import { IUpdateFeeKindLookupListFormValues } from '../../../../../interfaces/lookup/feeKind';
import { IUpdateFeeKind } from '../../../../../interfaces/lookup/feeKind/data';
import { ContentHeader } from '../../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { FetchResult } from '../../../../../shared/components/fetchResult/FetchResult';
import { ROW_GUTTER } from '../../../../../shared/Constants';
import { isError } from '../../../../../shared/helpers';
import { AppDispatch, RootState } from '../../../../../store';
import { fetchKindOfFeeById, updateKindOfFee } from '../../../../../store/lookupSlice/LookupActions';
import { resetState } from '../../../../../store/lookupSlice/LookupSlice';
import { NotFound } from '../../../../notFound/NotFound';

export const KindOfFeeLookupListEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const lookupSlice = useSelector((state: RootState) => state.lookupSlice);
    const itemId = params.id ? parseInt(params.id) : undefined;

    const onFinish = (values: IUpdateFeeKindLookupListFormValues) => {
        const body: IUpdateFeeKind = {
            id: lookupSlice.detailsPage.kindOfFee.data?.id,
            ...(values as any),
        };

        dispatch(updateKindOfFee(body));
    };

    useEffect(() => {
        if (lookupSlice.detailsPage.kindOfFee.updateStatus === StatePieceStatus.Success) {
            navigate('/admin/lookup-lists/kind-of-fee', { replace: true });
        }
    }, [lookupSlice.detailsPage.kindOfFee.updateStatus]);

    useEffect(() => {
        if (itemId) {
            dispatch(fetchKindOfFeeById(itemId));
        }

        return () => {
            dispatch(resetState());
        };
    }, [itemId]);

    useEffect(() => {
        if (lookupSlice.detailsPage.kindOfFee.data) {
            form.setFieldsValue({
                id: lookupSlice.detailsPage.kindOfFee.data.id,
                code: lookupSlice.detailsPage.kindOfFee.data.code,
                description: lookupSlice.detailsPage.kindOfFee.data.description,
            } as IUpdateFeeKindLookupListFormValues);
        }
    }, [lookupSlice.detailsPage.kindOfFee.data]);

    if (lookupSlice.detailsPage.kindOfFee.fetchStatus === StatePieceStatus.Error) {
        return <Result status="warning" title="Data wasn't found or something went wrong." />;
    }

    if (!itemId) {
        return <NotFound />;
    }

    return (
        <Form
            form={form}
            name="kindoffee-lookup-edit"
            onFinish={onFinish}
            layout="vertical"
            onValuesChange={(changedValue: any, values) => {
                form.setFieldsValue(values);
            }}
        >
            <ContentHeader
                title="Manage kind of fees lookup list"
                breadcrumbs={[
                    {
                        breadcrumbName: 'Admin',
                        path: '/admin',
                    },
                    {
                        breadcrumbName: 'Manage lookup lists',
                        path: '/admin/lookup-lists',
                    },
                    {
                        breadcrumbName: 'Kinf of fee',
                        path: '/admin/lookup-lists/kind-of-fee',
                    },
                    {
                        breadcrumbName: lookupSlice.detailsPage.kindOfFee.data?.description || '-',
                        path: `/admin/lookup-lists/kind-of-fee/${params.id}`,
                    },
                    {
                        breadcrumbName: 'Edit',
                        path: `/admin/lookup-lists/kind-of-fee/${params.id}/edit`,
                    },
                ]}
            >
                {!isError(lookupSlice.detailsPage.kindOfFee.fetchStatus) && (
                    <Form.Item>
                        <Space>
                            <Button
                                icon={<CloseOutlined />}
                                onClick={() => navigate(-1)}
                                disabled={
                                    lookupSlice.detailsPage.kindOfFee.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.kindOfFee.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Close
                            </Button>
                            <Button
                                htmlType="submit"
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={
                                    lookupSlice.detailsPage.kindOfFee.updateStatus === StatePieceStatus.IsFetching ||
                                    lookupSlice.detailsPage.kindOfFee.fetchStatus === StatePieceStatus.IsFetching
                                }
                            >
                                Save and close
                            </Button>
                        </Space>
                    </Form.Item>
                )}
            </ContentHeader>

            <div className="page-content">
                <Spin spinning={lookupSlice.detailsPage.kindOfFee.fetchStatus === StatePieceStatus.IsFetching}>
                    {isError(lookupSlice.detailsPage.kindOfFee.fetchStatus) ? (
                        <FetchResult status={lookupSlice.detailsPage.kindOfFee.fetchStatus} about={'Kind of fee'} />
                    ) : (
                        <>
                            <Row>
                                <Col span={24}>
                                    <Space>
                                        <label className="group-label">Kind of fee Information</label>
                                    </Space>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property required">Code</label>
                                    <Form.Item name="code" rules={[{ required: true, message: "'Code' is required" }]}>
                                        <Input maxLength={10} />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>
                                    <label className="property required">Description</label>
                                    <Form.Item name="description" rules={[{ required: true, message: "'Description' is required" }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>

                            {/* INTERNAL INFORMATION */}
                            <Row className="margin-top">
                                <Col span={24}>
                                    <label className="group-label">Internal Information</label>
                                </Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Created by</label>
                                    <p>{lookupSlice.detailsPage.kindOfFee?.data?.createdBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Created on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.kindOfFee.data?.created
                                            ? dayjs(lookupSlice.detailsPage.kindOfFee.data?.created).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                            <Row gutter={ROW_GUTTER}>
                                <Col span={8}>
                                    <label className="property">Last modified by</label>
                                    <p>{lookupSlice.detailsPage.kindOfFee.data?.modifiedBy?.name || '-'}</p>
                                </Col>
                                <Col span={8}>
                                    <label className="property">Last modified on</label>
                                    <p className="capitalize">
                                        {lookupSlice.detailsPage.kindOfFee.data?.modified
                                            ? dayjs(lookupSlice.detailsPage.kindOfFee.data?.modified).format('MMMM D, YYYY HH:mm')
                                            : '-'}
                                    </p>
                                </Col>
                                <Col span={8}>&nbsp;</Col>
                            </Row>
                        </>
                    )}
                </Spin>
            </div>
        </Form>
    );
};
